import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, Users, Mail, RefreshCw, Calendar, Phone, MapPin, BookOpen, ChevronDown, ChevronUp, Lock, LogOut, Clock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const ADMIN_USER = "admin";
const ADMIN_PASS = "iru2026";

const branchColors = {
  "Voleybol": "bg-amber-100 text-amber-800",
  "Basketbol": "bg-orange-100 text-orange-800",
  "Cimnastik": "bg-pink-100 text-pink-800",
  "Yaz Okulu": "bg-emerald-100 text-emerald-800",
};

function formatDate(dateStr) {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  } catch {
    return dateStr;
  }
}

export default function AdminPage() {
  const [authenticated, setAuthenticated] = useState(() => sessionStorage.getItem("iru_admin") === "true");
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [activeTab, setActiveTab] = useState("registrations");
  const [registrations, setRegistrations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [filterBranch, setFilterBranch] = useState("Tümü");

  const handleLogin = (e) => {
    e.preventDefault();
    if (loginForm.username === ADMIN_USER && loginForm.password === ADMIN_PASS) {
      sessionStorage.setItem("iru_admin", "true");
      setAuthenticated(true);
    } else {
      toast.error("Kullanıcı adı veya şifre hatalı.");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("iru_admin");
    setAuthenticated(false);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [regRes, conRes, trialRes] = await Promise.all([
        axios.get(`${API}/api/pre-registrations`),
        axios.get(`${API}/api/contacts`),
        axios.get(`${API}/api/trial-registrations`),
      ]);
      setRegistrations(regRes.data);
      setContacts(conRes.data);
      setTrials(trialRes.data);
    } catch (err) {
      console.error("Veri çekilemedi:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (authenticated) fetchData(); }, [authenticated]);

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] pt-20 flex items-center justify-center" data-testid="admin-login">
        <div className="w-full max-w-sm mx-4">
          <div className="bg-white border border-zinc-200 shadow-lg">
            <div className="bg-burgundy p-6 text-center">
              <Lock className="w-10 h-10 text-white mx-auto mb-2" strokeWidth={1.5} />
              <h1 className="font-heading text-xl font-bold uppercase tracking-wider text-white">Yönetim Paneli</h1>
              <p className="text-white/60 text-xs mt-1">Giriş yapın</p>
            </div>
            <form onSubmit={handleLogin} className="p-6 space-y-4">
              <div>
                <Label htmlFor="username" className="text-xs font-accent uppercase tracking-wider text-gray-500">Kullanıcı Adı</Label>
                <Input
                  id="username"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                  data-testid="admin-username"
                  className="mt-1.5 rounded-none border-zinc-300 h-11"
                  placeholder="Kullanıcı adı"
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-xs font-accent uppercase tracking-wider text-gray-500">Şifre</Label>
                <Input
                  id="password"
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  data-testid="admin-password"
                  className="mt-1.5 rounded-none border-zinc-300 h-11"
                  placeholder="Şifre"
                />
              </div>
              <Button
                type="submit"
                data-testid="admin-login-btn"
                className="w-full bg-burgundy hover:bg-burgundy-700 text-white font-heading uppercase tracking-wider rounded-none h-11"
              >
                Giriş Yap
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  const filteredRegistrations = filterBranch === "Tümü"
    ? registrations
    : registrations.filter(r => r.branch === filterBranch);

  const branches = ["Tümü", "Voleybol", "Basketbol", "Cimnastik", "Yaz Okulu"];

  const stats = {
    total: registrations.length,
    voleybol: registrations.filter(r => r.branch === "Voleybol").length,
    basketbol: registrations.filter(r => r.branch === "Basketbol").length,
    cimnastik: registrations.filter(r => r.branch === "Cimnastik").length,
    yazokulu: registrations.filter(r => r.branch === "Yaz Okulu").length,
    contacts: contacts.length,
    trials: trials.length,
  };

  const downloadExcel = async (type) => {
    const url = type === "registrations"
      ? `${API}/api/pre-registrations/export`
      : type === "contacts"
      ? `${API}/api/contacts/export`
      : `${API}/api/trial-registrations/export`;
    const filenames = {
      registrations: "iru_on_kayitlar.xlsx",
      contacts: "iru_iletisim_mesajlari.xlsx",
      trials: "iru_deneme_antrenman.xlsx",
    };
    try {
      const res = await axios.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filenames[type] || "export.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Excel indirilemedi:", err);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0] pt-20" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight text-[#1A1A1A]">
              Yönetim Paneli
            </h1>
            <p className="text-sm text-zinc-500 mt-1">Ön kayıt ve iletişim verilerini yönetin</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={fetchData}
              disabled={loading}
              data-testid="refresh-btn"
              variant="outline"
              className="rounded-none border-zinc-300 text-sm"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Yenile
            </Button>
            <Button
              onClick={handleLogout}
              data-testid="logout-btn"
              variant="outline"
              className="rounded-none border-red-200 text-red-600 hover:bg-red-50 text-sm"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Çıkış
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="admin-stats">
          {[
            { label: "Toplam Kayıt", value: stats.total, color: "border-burgundy" },
            { label: "Voleybol", value: stats.voleybol, color: "border-amber-500" },
            { label: "Basketbol", value: stats.basketbol, color: "border-orange-500" },
            { label: "Cimnastik", value: stats.cimnastik, color: "border-pink-500" },
            { label: "Yaz Okulu", value: stats.yazokulu, color: "border-emerald-500" },
            { label: "Mesajlar", value: stats.contacts, color: "border-blue-500" },
            { label: "Deneme", value: stats.trials, color: "border-purple-500" },
          ].map((s) => (
            <div key={s.label} className={`bg-white border-l-4 ${s.color} p-4 shadow-sm`}>
              <p className="text-2xl font-bold text-[#1A1A1A]">{s.value}</p>
              <p className="text-xs text-zinc-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setActiveTab("registrations")}
            data-testid="tab-registrations"
            className={`rounded-none text-sm font-heading uppercase tracking-wider ${
              activeTab === "registrations"
                ? "bg-burgundy text-white"
                : "bg-white text-zinc-600 hover:bg-zinc-100 border border-zinc-200"
            }`}
          >
            <Users className="w-4 h-4 mr-2" />
            Ön Kayıtlar ({registrations.length})
          </Button>
          <Button
            onClick={() => setActiveTab("contacts")}
            data-testid="tab-contacts"
            className={`rounded-none text-sm font-heading uppercase tracking-wider ${
              activeTab === "contacts"
                ? "bg-burgundy text-white"
                : "bg-white text-zinc-600 hover:bg-zinc-100 border border-zinc-200"
            }`}
          >
            <Mail className="w-4 h-4 mr-2" />
            Mesajlar ({contacts.length})
          </Button>
          <Button
            onClick={() => setActiveTab("trials")}
            data-testid="tab-trials"
            className={`rounded-none text-sm font-heading uppercase tracking-wider ${
              activeTab === "trials"
                ? "bg-burgundy text-white"
                : "bg-white text-zinc-600 hover:bg-zinc-100 border border-zinc-200"
            }`}
          >
            <Clock className="w-4 h-4 mr-2" />
            Deneme ({trials.length})
          </Button>
        </div>

        {/* Registrations Tab */}
        {activeTab === "registrations" && (
          <div className="bg-white border border-zinc-200 shadow-sm" data-testid="registrations-table">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 border-b border-zinc-200">
              <div className="flex flex-wrap gap-2">
                {branches.map((b) => (
                  <button
                    key={b}
                    onClick={() => setFilterBranch(b)}
                    data-testid={`filter-${b.toLowerCase().replace(/\s/g, "-")}`}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                      filterBranch === b
                        ? "bg-burgundy text-white"
                        : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                    }`}
                  >
                    {b}
                  </button>
                ))}
              </div>
              <Button
                onClick={() => downloadExcel("registrations")}
                data-testid="export-registrations"
                className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
              >
                <Download className="w-4 h-4 mr-2" />
                Excel İndir
              </Button>
            </div>

            {filteredRegistrations.length === 0 ? (
              <div className="p-12 text-center text-zinc-400">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Henüz kayıt bulunmuyor</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-zinc-200">
                      <th className="text-left p-3 text-xs font-heading uppercase tracking-wider text-zinc-500">Öğrenci</th>
                      <th className="text-left p-3 text-xs font-heading uppercase tracking-wider text-zinc-500">Branş</th>
                      <th className="text-left p-3 text-xs font-heading uppercase tracking-wider text-zinc-500 hidden md:table-cell">Veli</th>
                      <th className="text-left p-3 text-xs font-heading uppercase tracking-wider text-zinc-500 hidden md:table-cell">Telefon</th>
                      <th className="text-left p-3 text-xs font-heading uppercase tracking-wider text-zinc-500 hidden lg:table-cell">Tarih</th>
                      <th className="p-3 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRegistrations.map((reg, idx) => (
                      <RegistrationRow key={reg.id || idx} reg={reg} idx={idx} expanded={expandedRow === idx} onToggle={() => setExpandedRow(expandedRow === idx ? null : idx)} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === "contacts" && (
          <div className="bg-white border border-zinc-200 shadow-sm" data-testid="contacts-table">
            <div className="flex items-center justify-between p-4 border-b border-zinc-200">
              <p className="text-sm font-heading uppercase tracking-wider text-zinc-500">Gelen Mesajlar</p>
              <Button
                onClick={() => downloadExcel("contacts")}
                data-testid="export-contacts"
                className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
              >
                <Download className="w-4 h-4 mr-2" />
                Excel İndir
              </Button>
            </div>

            {contacts.length === 0 ? (
              <div className="p-12 text-center text-zinc-400">
                <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Henüz mesaj bulunmuyor</p>
              </div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {contacts.map((msg, idx) => (
                  <div key={msg.id || idx} className="p-4 hover:bg-zinc-50 transition-colors" data-testid={`contact-row-${idx}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <p className="font-semibold text-[#1A1A1A]">{msg.name}</p>
                          <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5">{msg.subject}</span>
                        </div>
                        <p className="text-sm text-zinc-600 mb-2">{msg.message}</p>
                        <div className="flex flex-wrap gap-3 text-xs text-zinc-400">
                          <span>{msg.email}</span>
                          {msg.phone && <span>{msg.phone}</span>}
                          <span>{formatDate(msg.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Trials Tab */}
        {activeTab === "trials" && (
          <div className="bg-white border border-zinc-200 shadow-sm" data-testid="trials-table">
            <div className="flex items-center justify-between p-4 border-b border-zinc-200">
              <p className="text-sm font-heading uppercase tracking-wider text-zinc-500">Deneme Antrenman Kayıtları</p>
              <Button
                onClick={() => downloadExcel("trials")}
                data-testid="export-trials"
                className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
              >
                <Download className="w-4 h-4 mr-2" />
                Excel İndir
              </Button>
            </div>

            {trials.length === 0 ? (
              <div className="p-12 text-center text-zinc-400">
                <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Henüz deneme antrenman kaydı bulunmuyor</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-zinc-50 border-b border-zinc-200">
                      <th className="text-left p-3 text-xs font-heading uppercase tracking-wider text-zinc-500">Sporcu</th>
                      <th className="text-left p-3 text-xs font-heading uppercase tracking-wider text-zinc-500">Gün</th>
                      <th className="text-left p-3 text-xs font-heading uppercase tracking-wider text-zinc-500">Saat</th>
                      <th className="text-left p-3 text-xs font-heading uppercase tracking-wider text-zinc-500 hidden md:table-cell">Veli</th>
                      <th className="text-left p-3 text-xs font-heading uppercase tracking-wider text-zinc-500 hidden md:table-cell">Telefon</th>
                      <th className="text-left p-3 text-xs font-heading uppercase tracking-wider text-zinc-500 hidden lg:table-cell">Lisans</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trials.map((t, idx) => (
                      <tr key={t.id || idx} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`trial-row-${idx}`}>
                        <td className="p-3">
                          <p className="font-semibold text-[#1A1A1A]">{t.athlete_name} {t.athlete_surname}</p>
                          <p className="text-xs text-zinc-400">{t.birth_date}</p>
                        </td>
                        <td className="p-3">
                          <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800">{t.trial_day}</span>
                        </td>
                        <td className="p-3">
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800">{t.trial_time}</span>
                        </td>
                        <td className="p-3 hidden md:table-cell text-zinc-600">{t.parent_name} {t.parent_surname}</td>
                        <td className="p-3 hidden md:table-cell text-zinc-600">{t.parent_phone}</td>
                        <td className="p-3 hidden lg:table-cell text-zinc-400">{t.has_license}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function RegistrationRow({ reg, idx, expanded, onToggle }) {
  return (
    <>
      <tr
        className={`border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer transition-colors ${expanded ? "bg-zinc-50" : ""}`}
        onClick={onToggle}
        data-testid={`registration-row-${idx}`}
      >
        <td className="p-3">
          <p className="font-semibold text-[#1A1A1A]">{reg.student_name} {reg.student_surname}</p>
          <p className="text-xs text-zinc-400 md:hidden">{reg.parent_name} {reg.parent_surname}</p>
        </td>
        <td className="p-3">
          <span className={`px-2 py-1 text-xs font-medium ${branchColors[reg.branch] || "bg-zinc-100 text-zinc-600"}`}>
            {reg.branch}
          </span>
        </td>
        <td className="p-3 hidden md:table-cell text-zinc-600">{reg.parent_name} {reg.parent_surname}</td>
        <td className="p-3 hidden md:table-cell text-zinc-600">{reg.parent_phone}</td>
        <td className="p-3 hidden lg:table-cell text-zinc-400 text-xs">{formatDate(reg.created_at)}</td>
        <td className="p-3">
          {expanded ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-zinc-50 border-b border-zinc-200">
          <td colSpan={6} className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              <div className="flex items-start gap-2">
                <Calendar className="w-4 h-4 text-zinc-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-zinc-400">Doğum Tarihi</p>
                  <p className="text-zinc-700">{reg.birth_date || "-"}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Users className="w-4 h-4 text-zinc-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-zinc-400">Cinsiyet</p>
                  <p className="text-zinc-700">{reg.gender || "-"}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Mail className="w-4 h-4 text-zinc-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-zinc-400">Veli E-posta</p>
                  <p className="text-zinc-700">{reg.parent_email || "-"}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-zinc-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-zinc-400">Veli Telefon</p>
                  <p className="text-zinc-700">{reg.parent_phone || "-"}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-zinc-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-zinc-400">Adres</p>
                  <p className="text-zinc-700">{reg.address || "-"}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <BookOpen className="w-4 h-4 text-zinc-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-zinc-400">Nereden Duydu</p>
                  <p className="text-zinc-700">{reg.how_did_you_hear || "-"}</p>
                </div>
              </div>
              {reg.health_notes && (
                <div className="sm:col-span-2 lg:col-span-3">
                  <p className="text-xs text-zinc-400 mb-1">Sağlık Notu</p>
                  <p className="text-zinc-700 bg-white p-2 border border-zinc-200">{reg.health_notes}</p>
                </div>
              )}
              {reg.notes && (
                <div className="sm:col-span-2 lg:col-span-3">
                  <p className="text-xs text-zinc-400 mb-1">Ek Notlar</p>
                  <p className="text-zinc-700 bg-white p-2 border border-zinc-200">{reg.notes}</p>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
